import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { LoginService } from '@shared-angular/services/login.service';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SILENT_HTTP_STATUSES } from '@shared-angular/interceptors/http-error.interceptor';
import { ChatbotConfig } from '@shared/index';
import { catchError, filter, firstValueFrom, of } from 'rxjs';

interface BotVisit { botId: string; visitedAt: number; }

@Injectable({ providedIn: 'root' })
export class RecentBotsService {
  constructor(private router: Router, private login: LoginService, private http: HttpClient) {
    // This application-scoped subscription also observes leaving a bot, so opening
    // it again counts as a new visit, whereas changing its tabs does not.
    let previousBotId: string | undefined;
    let previousUserId: string | undefined;
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      let route = this.router.routerState.snapshot.root;
      let bot: ChatbotConfig | undefined;
      while (route.firstChild) {
        route = route.firstChild;
        if (route.data['selectedBot']) bot = route.data['selectedBot'] as ChatbotConfig;
      }
      const userId = this.login.loggedInUser?._id;
      if (bot && userId && (bot._id !== previousBotId || userId !== previousUserId)
        && !bot.archived && !bot.thisIsTrash && !bot.isTemplate && !bot.isDemo) {
        this.record(bot._id);
      }
      previousBotId = bot?._id;
      previousUserId = userId;
    });
  }

  private key(): string | null {
    const userId = this.login.loggedInUser?._id;
    return userId ? `bot-admin-recent-bots:${userId}` : null;
  }

  private read(): BotVisit[] {
    try {
      const key = this.key();
      const value: unknown = key ? JSON.parse(localStorage.getItem(key) ?? '[]') : [];
      if (!Array.isArray(value)) return [];
      const visits = value.filter((entry: unknown): entry is BotVisit =>
        !!entry && typeof entry === 'object' && 'botId' in entry && 'visitedAt' in entry
        && typeof entry.botId === 'string' && /^[a-f0-9]{24}$/i.test(entry.botId)
        && typeof entry.visitedAt === 'number' && Number.isFinite(entry.visitedAt));
      return visits.sort((a, b) => b.visitedAt - a.visitedAt)
        .filter((entry, index, all) => all.findIndex(other => other.botId === entry.botId) === index).slice(0, 5);
    } catch {
      return [];
    }
  }

  private record(botId: string): void {
    const key = this.key();
    if (!key) return;
    const visits = [{ botId, visitedAt: Date.now() }, ...this.read().filter(visit => visit.botId !== botId)].slice(0, 5);
    try {
      localStorage.setItem(key, JSON.stringify(visits));
    } catch {
      // Browsing must also work without local storage.
    }
  }

  public async load(): Promise<ChatbotConfig[]> {
    const key = this.key();
    const bots = await Promise.all(this.read().map(visit => firstValueFrom(
      this.http.get<ChatbotConfig>(`/api/toolbox/bot/admin/botconfig/${visit.botId}`, {
        context: new HttpContext().set(SILENT_HTTP_STATUSES, [403, 404])
      }).pipe(catchError(() => of(null)))
    )));
    if (key !== this.key()) return [];
    return bots.filter((bot): bot is ChatbotConfig => !!bot
      && !bot.archived && !bot.thisIsTrash && !bot.isTemplate && !bot.isDemo);
  }
}
